import { collection, doc, DocumentReference, Firestore } from 'firebase/firestore';
import { Course } from '../types';

export const normalizeAcademicYear = (value: string | number | undefined | null): string =>
  (String(value ?? '').match(/\d{4}/) || [])[0] || '';

export const normalizeSemester = (value: string | undefined | null): string =>
  String(value ?? '').trim().replace(/学期$/, '');

export const getCourseId = (course: Pick<Course, 'id'> & { courseId?: string }): string =>
  course.courseId || course.id;

export const getOfferingKey = (course: Pick<Course, 'id' | 'academicYear'> & { courseId?: string }): string => {
  const year = normalizeAcademicYear(course.academicYear);
  const id = getCourseId(course);
  if (!year || !id) throw new Error('course id and academicYear are required');
  return `${year}_${id}`;
};

/**
 * Stable identity for a rendered course slot.
 *
 * A course can be expanded into multiple rows when it meets on more than one
 * day/period. The course/offering identity alone is therefore not sufficient
 * for list virtualization keys.
 */
export const getCourseSlotKey = (
  course: Pick<Course, 'id' | 'academicYear' | 'semester' | 'dayOfWeek' | 'period'> & { courseId?: string }
): string => {
  const year = normalizeAcademicYear(course.academicYear);
  const semester = normalizeSemester(course.semester);
  const id = getCourseId(course);
  const day = String(course.dayOfWeek ?? 'unknown');
  const period = String(course.period ?? 'unknown');

  return `${year}_${semester}_${id}_${day}_${period}`;
};

export const getGradeSubmissionKey = (
  course: Pick<Course, 'id' | 'academicYear'> & { courseId?: string },
  semester: string | undefined | null
): string => {
  const year = normalizeAcademicYear(course.academicYear);
  const normalizedSemester = normalizeSemester(semester);
  const id = getCourseId(course);
  if (!year || !normalizedSemester || !id) {
    throw new Error('course id, academicYear, and semester are required');
  }
  return `${year}_${normalizedSemester}_${id}`;
};

export const courseParent = (db: Firestore, courseId: string): DocumentReference =>
  doc(db, 'courses', courseId);

export const offeringDocument = (
  db: Firestore,
  course: Pick<Course, 'id' | 'academicYear'> & { courseId?: string }
): DocumentReference => {
  const courseId = getCourseId(course);
  const year = normalizeAcademicYear(course.academicYear);
  if (!courseId || !year) throw new Error('course id and academicYear are required');
  return doc(db, 'courses', courseId, 'offerings', year);
};

export const offeringCollection = (
  db: Firestore,
  course: Pick<Course, 'id' | 'academicYear'> & { courseId?: string },
  child: 'reviews' | 'grades' | 'surveyResponses'
) => collection(offeringDocument(db, course), child);

export const surveyResponsesCollection = (
  db: Firestore,
  course: Pick<Course, 'id' | 'academicYear'> & { courseId?: string }
) => collection(offeringDocument(db, course), 'surveyResponses');

export const surveyResponseDocument = (
  db: Firestore,
  course: Pick<Course, 'id' | 'academicYear'> & { courseId?: string },
  sourceResponseId: string
) => doc(surveyResponsesCollection(db, course), sourceResponseId);
